import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    FaSearch, FaFileInvoiceDollar, FaMoneyBillWave, FaClock, FaFileAlt, FaWallet,
    FaExclamationTriangle, FaChartPie, FaUniversity, FaHandHoldingUsd, FaListUl
} from 'react-icons/fa';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, CartesianGrid } from 'recharts';
import ErpTemplate from '../../../components/ErpTemplate';
import ReactSelect from '../../../components/ReactSelect';
import Data from '../../../components/Data';
import { FinField } from '../../../components/FinField';
import { OrigemMoeda } from '../../../components/ValorComOrigem';
import { useSetFullBreadcrumbTrail } from '../../../context/BreadcrumbContext';
import api from '../../../services/api';
import { periodoPainel, momentPainel } from '../painelFinanceiroPeriodo';
import '../../../css/financeiro-parcela-pagar.css';

// Raio-X financeiro do evento (treinamento 07/10, item 14): a saúde financeira de um
// evento — ou o consolidado do cliente quando nenhum evento está escolhido — num
// lugar só. Só leitura: GET /financeiro/raioX (RaioXFinanceiroEvento na API).
// Spec: accessapi docs/superpowers/specs/2026-10-08-raio-x-financeiro-evento-design.md

const PERIODO_PADRAO = { dataInicio: '01/01/2026', dataFim: '31/12/2026' };
const CORES_ETAPA = ['#a85b07', '#5a8dee', '#c1232b', '#15803d'];
const CORES_DOC = { ND: '#5a8dee', NF: '#15803d', 'Não informado': '#94a3b8' };

const moeda = (v) => 'R$ ' + (parseFloat(v) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const dataBr = (v) => (v ? String(v).slice(0, 10).split('-').reverse().join('/') : '—');

function lerUrl(search) {
    const q = new URLSearchParams(search);
    const isoParaBr = (v) => (v && /^\d{4}-\d{2}-\d{2}/.test(v) ? v.slice(0, 10).split('-').reverse().join('/') : v);
    return {
        cliente: q.get('cliente') || '',
        evento: q.get('evento') || '',
        dataInicio: isoParaBr(q.get('dataInicio')) || (q.has('dataInicio') ? '' : PERIODO_PADRAO.dataInicio),
        dataFim: isoParaBr(q.get('dataFim')) || (q.has('dataFim') ? '' : PERIODO_PADRAO.dataFim),
    };
}

function Kpi({ label, valor, sub, icone, tom }) {
    return (
        <div className="fin-pp__kpi">
            <div className="fin-pp__kpi-top">
                <span className="fin-pp__kpi-label">{label}</span>
                <span className="fin-pp__kpi-ic">{icone}</span>
            </div>
            {/* 6 KPIs numa fileira: valor menor e sem quebra (o padrão de 27px quebrava "R$ 813.648,00"). */}
            <div className={'fin-pp__kpi-value' + (tom ? ' is-' + tom : '')} style={{ fontSize: 21, whiteSpace: 'nowrap' }}>{valor}</div>
            {sub ? <div className="fin-pp__kpi-sub">{sub}</div> : null}
        </div>
    );
}

function Secao({ icone, titulo, sub, pill, children }) {
    return (
        <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
            <div className="fin-pp__cphead">
                <span className="fin-pp__cphead-ic">{icone}</span>
                <div style={{ minWidth: 0 }}>
                    <h2 className="fin-pp__cphead-title">{titulo}</h2>
                    {sub ? <p className="fin-pp__cphead-sub">{sub}</p> : null}
                </div>
                {pill}
            </div>
            <div style={{ padding: '0 14px 14px' }}>{children}</div>
        </div>
    );
}

export default function PainelRaioXFinanceiro() {
    const history = useHistory();
    const location = useLocation();
    const token = localStorage.getItem('token');
    const setFullTrail = useSetFullBreadcrumbTrail();

    const inicial = lerUrl(location.search);
    const [filtro, setFiltro] = useState({ ...inicial, nomeCliente: '', nomeEvento: '' });
    const [eventosLista, setEventosLista] = useState([]);
    const [dados, setDados] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState('');
    const periodo = periodoPainel(filtro);

    useEffect(() => {
        setFullTrail([{ path: '/financeiro', label: 'Financeiro' }, { path: '/financeiroRaioX', label: 'Raio-X financeiro' }]);
        // eslint-disable-next-line
    }, []);

    // Eventos para o filtro: a lista já abre carregada, sem precisar escolher o cliente nem digitar
    // (Rafael, 10/10) — os mesmos da listagem de eventos (abertos, pendentes e fechados; cancelado
    // fica fora, como no cálculo), do período, mais recentes primeiro. A API devolve até 100 por vez:
    // digitar busca no servidor entre todos. Com cliente escolhido, só os dele.
    const eventosVistos = useRef({}); // id → evento (p/ achar o cliente do evento escolhido)
    const paramsEventos = (nome) => {
        const p = { resumoPainel: true, pageSize: 100, situacao: ['ativo', 'pendente', 'fechado'], orderBy: JSON.stringify({ data: 'desc' }) };
        if (filtro.cliente) p.cliente = filtro.cliente;
        if (periodo.params && periodo.params.dataInicio) p.dataInicial = periodo.params.dataInicio;
        if (periodo.params && periodo.params.dataFim) p.dataFinal = periodo.params.dataFim;
        if (nome) p.nome = nome;
        return p;
    };
    const clienteDoEvento = (ev) => (ev && ev.cliente && typeof ev.cliente === 'object') ? ev.cliente : null;
    const opcaoEvento = (ev) => {
        eventosVistos.current[String(ev.id)] = ev;
        const cli = clienteDoEvento(ev);
        return {
            value: ev.id,
            label: (ev.codigo ? '#' + ev.codigo + ' ' : '') + ev.nome
                + (!filtro.cliente && cli && cli.nome ? ' · ' + cli.nome : '')
                + (ev.situacao && ev.situacao !== 'ativo' ? ' (' + ev.situacao + ')' : ''),
        };
    };
    const buscarEventos = (nome) => api({ method: 'get', action: '/evento', token: token, params: paramsEventos(nome) })
        .then(r => (r.data.data || []).map(opcaoEvento))
        .catch(() => []);
    useEffect(() => {
        buscarEventos('').then(setEventosLista);
        // eslint-disable-next-line
    }, [filtro.cliente, periodo.params && periodo.params.dataInicio, periodo.params && periodo.params.dataFim]);

    const buscar = (f) => {
        const atual = f || filtro;
        const per = periodoPainel(atual);
        if (!atual.cliente) { setDados(null); return; }
        if (per.erro) { setErro(per.erro); return; }
        // Filtros na URL: o link (e o "voltar") abre o mesmo raio-X.
        const q = new URLSearchParams();
        q.set('cliente', atual.cliente);
        if (atual.evento) q.set('evento', atual.evento);
        if (per.params.dataInicio) q.set('dataInicio', per.params.dataInicio);
        if (per.params.dataFim) q.set('dataFim', per.params.dataFim);
        history.replace('/financeiroRaioX?' + q.toString());

        setCarregando(true);
        setErro('');
        api({ method: 'get', action: '/financeiro/raioX', token: token,
            params: { cliente: atual.cliente, evento: atual.evento || undefined, ...per.params } })
            .then(r => {
                const d = r.data.data;
                setDados(d);
                const f2 = { ...atual };
                if (d && d.filtro && d.filtro.cliente) f2.nomeCliente = d.filtro.cliente.nome;
                if (d && d.filtro && d.filtro.evento) f2.nomeEvento = d.filtro.evento.nome;
                setFiltro(f2);
            })
            .catch(e => {
                const msg = (e.response && e.response.data && e.response.data.mensagem) || 'Erro ao montar o raio-X financeiro.';
                setErro(msg);
                setDados(null);
                toast.error(msg);
            })
            .finally(() => setCarregando(false));
    };

    useEffect(() => {
        if (inicial.cliente) buscar();
        // eslint-disable-next-line
    }, []);

    const abrirEvento = (ev) => {
        const f = { ...filtro, evento: ev.id, nomeEvento: ev.nome };
        setFiltro(f);
        buscar(f);
        window.scrollTo(0, 0);
    };

    const verConsolidado = () => {
        const f = { ...filtro, evento: '', nomeEvento: '' };
        setFiltro(f);
        buscar(f);
    };

    const t = dados ? dados.total : null;
    const ehEvento = !!(dados && dados.filtro && dados.filtro.evento);
    const eventoAtual = ehEvento ? dados.filtro.evento : null;
    const nomeCliente = (dados && dados.filtro && dados.filtro.cliente && dados.filtro.cliente.nome) || filtro.nomeCliente;

    const irContasPagar = () => {
        if (!eventoAtual) return;
        history.push('/financeiroParcelaPagar?idEvento=' + eventoAtual.id + '&nomeEvento=' + encodeURIComponent(eventoAtual.nome || ''));
    };

    const dadosEtapas = t ? [
        { etapa: 'Custo Previsto', valor: t.contas_pagar.custo_previsto.valor },
        { etapa: 'Contas Pendentes', valor: t.contas_pagar.pendentes.valor },
        { etapa: 'Vencidas', valor: t.contas_pagar.vencidas.valor },
        { etapa: 'Pagas', valor: t.contas_pagar.pagas.valor },
    ] : [];
    const dadosNdNf = t ? [
        { nome: 'ND', valor: t.nd_nf_fornecedor.nd },
        { nome: 'NF', valor: t.nd_nf_fornecedor.nf },
        { nome: 'Não informado', valor: t.nd_nf_fornecedor.nao_informado },
    ].filter(x => x.valor > 0) : [];

    return (
        <ErpTemplate>
            <div className="main-content container-fluid fin-pp">
                <div className="fin-pp__head">
                    <div>
                        <h1 className="fin-pp__title">Raio-X financeiro</h1>
                        <p className="fin-pp__subtitle">
                            {nomeCliente
                                ? (eventoAtual ? nomeCliente + ' · ' + (eventoAtual.codigo ? '#' + eventoAtual.codigo + ' ' : '') + eventoAtual.nome : nomeCliente + ' · consolidado de todos os eventos do período')
                                : 'Saúde financeira por cliente ou evento'}
                        </p>
                    </div>
                    {ehEvento && (
                        <div className="fin-pp__actions">
                            <button type="button" className="fin-pp__btn fin-pp__btn--pill fin-pp__btn--sm" onClick={verConsolidado}>
                                Ver consolidado do cliente
                            </button>
                        </div>
                    )}
                </div>

                {/* Filtros — cliente → evento → período, sem sair da tela */}
                <div className="fin-pp__card" style={{ padding: 14, marginBottom: 16 }}>
                    <div className="fin-pp__filters" style={{ alignItems: 'flex-end' }}>
                        <FinField label="Cliente" full>
                            {/* Lista já aberta ao clicar (clientes ativos — poucos), sem precisar digitar (10/10) */}
                            <ReactSelect
                                defaultOptions={true}
                                placeholder="Escolha o cliente"
                                value={filtro.cliente ? { value: filtro.cliente, label: filtro.nomeCliente || 'Cliente' } : null}
                                onChange={e => {
                                    // Escolher o cliente já mostra o consolidado dele.
                                    const f = { ...filtro, cliente: e ? e.value : '', nomeCliente: e ? e.label : '', evento: '', nomeEvento: '' };
                                    setFiltro(f);
                                    if (f.cliente) buscar(f); else setDados(null);
                                }}
                                loadOptions={(input) => api({ method: 'get', action: '/participante', token: token, params: { nome: input, tipoCadastro: 'cliente', situacaoRegistro: 'ativo' } })
                                    .then(r => (r.data.data || []).map(c => ({
                                        // Mesmo texto do cliente no cadastro de evento (CadastroEvento.js)
                                        value: c.id,
                                        label: c.codigo + ' - ' + c.nome
                                            + (c.enderecomunicipio ? ' - ' + c.enderecomunicipio.nome + '-' + c.enderecomunicipio.uf : '')
                                            + (c.cnpj ? ' (' + c.cnpj + ')' : '')
                                            + (c.cpf ? ' (' + c.cpf + ')' : '')
                                            + (c.nomefantasia ? ' - ' + c.nomefantasia : ''),
                                    })))}
                            />
                        </FinField>
                    </div>
                    <div className="fin-pp__filters" style={{ alignItems: 'flex-end' }}>
                        <FinField label="Evento" grow>
                            {/* key: refaz o select (e o cache da busca) quando cliente/período mudam */}
                            <React.Fragment key={'ev-' + (filtro.cliente || 'todos') + '-' + (periodo.params ? (periodo.params.dataInicio || '') + (periodo.params.dataFim || '') : '')}>
                            <ReactSelect
                                placeholder={filtro.cliente ? 'Todos os eventos (consolidado)' : 'Escolha o evento'}
                                value={filtro.evento ? { value: filtro.evento, label: filtro.nomeEvento || 'Evento' }
                                    : (filtro.cliente ? { value: '', label: 'Todos os eventos (consolidado)' } : null)}
                                onChange={e => {
                                    const ev = e && e.value ? eventosVistos.current[String(e.value)] : null;
                                    const cli = clienteDoEvento(ev);
                                    // Evento escolhido sem cliente: o cliente vem do próprio evento.
                                    const f = { ...filtro, evento: e ? e.value : '', nomeEvento: e && e.value ? (ev ? (ev.codigo ? '#' + ev.codigo + ' ' : '') + ev.nome : e.label) : '' };
                                    if (ev && cli && String(cli.id) !== String(filtro.cliente)) {
                                        f.cliente = String(cli.id);
                                        f.nomeCliente = cli.nome || 'Cliente';
                                    }
                                    setFiltro(f);
                                    if (f.cliente) buscar(f);
                                }}
                                defaultOptions={(filtro.cliente ? [{ value: '', label: 'Todos os eventos (consolidado)' }] : []).concat(eventosLista)}
                                loadOptions={(input) => buscarEventos(input)}
                            />
                            </React.Fragment>
                        </FinField>
                        <Data label="Data início" value={filtro.dataInicio} onChange={d => setFiltro({ ...filtro, dataInicio: d })} parse={momentPainel} />
                        <Data label="Data fim" value={filtro.dataFim} onChange={d => setFiltro({ ...filtro, dataFim: d })} parse={momentPainel} />
                        <div className="fin-pp__field fin-pp__field--auto">
                            <button type="button" className="fin-pp__btn fin-pp__btn--primary fin-pp__btn--tall"
                                disabled={!filtro.cliente || !!periodo.erro || carregando} onClick={() => buscar()}>
                                <FaSearch size={13} /> {carregando ? 'Carregando…' : 'Ver raio-X'}
                            </button>
                        </div>
                    </div>
                    {periodo.erro && <div className="fin-pp__cell-muted" role="alert" style={{ color: 'var(--fin-wn)', marginTop: 8 }}>{periodo.erro}</div>}
                </div>

                {!filtro.cliente && !dados && (
                    <div className="fin-pp__card fin-pp__empty" style={{ padding: 40 }}>Escolha um evento ou um cliente para ver o raio-X financeiro.</div>
                )}
                {erro && !carregando && <div className="fin-pp__card fin-pp__empty" style={{ padding: 30, color: 'var(--fin-dg)' }}>{erro}</div>}
                {dados && dados.eventos.length === 0 && !carregando && (
                    <div className="fin-pp__card fin-pp__empty" style={{ padding: 40 }}>Este cliente não tem eventos no período.</div>
                )}

                {t && dados.eventos.length > 0 && (
                    <>
                        <section className="fin-pp__kpis" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(170px, 100%), 1fr))', marginBottom: 16 }}>
                            <Kpi label="Total do orçamento" icone={<FaFileInvoiceDollar size={16} />} valor={moeda(t.orcamento.total)}
                                sub={<>{t.orcamento.itens} itens · {t.orcamento.fornecedores} fornecedores<OrigemMoeda origem={t.origem_moeda} inline /></>} />
                            <Kpi label="Pago" tom="ok" icone={<FaMoneyBillWave size={16} />} valor={moeda(t.contas_pagar.pagas.valor)}
                                sub={t.contas_pagar.pagas.qtd + ' pagamentos feitos'} />
                            <Kpi label="A pagar" tom="warn" icone={<FaClock size={16} />} valor={moeda(t.contas_pagar.a_pagar.valor)}
                                sub={t.contas_pagar.a_pagar.qtd + ' pagamentos faltando'} />
                            <Kpi label="Adiantamento creditado" tom="info" icone={<FaHandHoldingUsd size={16} />} valor={moeda(t.adiantamento.creditado)}
                                sub={t.adiantamento.em_aberto > 0 ? moeda(t.adiantamento.em_aberto) + ' ainda não caiu' : 'nada em aberto'} />
                            <Kpi label="Saldo do evento" tom={t.saldo < 0 ? 'danger' : 'ok'} icone={<FaWallet size={16} />} valor={moeda(t.saldo)}
                                sub="disponível na conta do evento" />
                            <Kpi label="Parcelas sem NF" tom={t.notas_fiscais.parcelas_sem_nf.qtd > 0 ? 'danger' : 'ok'} icone={<FaExclamationTriangle size={16} />}
                                valor={String(t.notas_fiscais.parcelas_sem_nf.qtd)}
                                sub={t.notas_fiscais.pagas_sem_nf.qtd > 0 ? t.notas_fiscais.pagas_sem_nf.qtd + ' já pagas sem NF' : moeda(t.notas_fiscais.parcelas_sem_nf.valor)} />
                        </section>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 16, marginBottom: 16 }}>
                            <Secao icone={<FaMoneyBillWave size={16} />} titulo="Contas a pagar" sub="Do plano de pagamento até a baixa"
                                pill={ehEvento ? <button type="button" className="fin-pp__btn fin-pp__btn--sm" style={{ marginLeft: 'auto' }} onClick={irContasPagar}>Abrir no Contas a Pagar</button> : null}>
                                <div className="fin-pp__tblwrap">
                                    <table className="fin-pp__tbl">
                                        <thead><tr><th>Etapa</th><th style={{ textAlign: 'right' }}>Qtd</th><th style={{ textAlign: 'right' }}>Valor</th></tr></thead>
                                        <tbody>
                                            <tr><td className="fin-pp__cell-strong">Custo Previsto</td><td style={{ textAlign: 'right' }}>{t.contas_pagar.custo_previsto.qtd}</td><td style={{ textAlign: 'right' }}>{moeda(t.contas_pagar.custo_previsto.valor)}</td></tr>
                                            <tr><td className="fin-pp__cell-muted" style={{ paddingLeft: 26 }}>aguardando aprovação</td><td style={{ textAlign: 'right' }}>{t.contas_pagar.custo_previsto.aguardando.qtd}</td><td style={{ textAlign: 'right' }}>{moeda(t.contas_pagar.custo_previsto.aguardando.valor)}</td></tr>
                                            <tr><td className="fin-pp__cell-muted" style={{ paddingLeft: 26 }}>aprovado, não liberado</td><td style={{ textAlign: 'right' }}>{t.contas_pagar.custo_previsto.aprovado.qtd}</td><td style={{ textAlign: 'right' }}>{moeda(t.contas_pagar.custo_previsto.aprovado.valor)}</td></tr>
                                            <tr><td className="fin-pp__cell-strong">Contas Pendentes</td><td style={{ textAlign: 'right' }}>{t.contas_pagar.pendentes.qtd}</td><td style={{ textAlign: 'right' }}>{moeda(t.contas_pagar.pendentes.valor)}</td></tr>
                                            <tr><td className="fin-pp__cell-muted" style={{ paddingLeft: 26 }}>vencidas</td><td style={{ textAlign: 'right', color: t.contas_pagar.vencidas.qtd ? 'var(--fin-dg)' : undefined }}>{t.contas_pagar.vencidas.qtd}</td><td style={{ textAlign: 'right' }}>{moeda(t.contas_pagar.vencidas.valor)}</td></tr>
                                            <tr><td className="fin-pp__cell-strong">Pagas</td><td style={{ textAlign: 'right' }}>{t.contas_pagar.pagas.qtd}</td><td style={{ textAlign: 'right', color: 'var(--fin-ok)' }}>{moeda(t.contas_pagar.pagas.valor)}</td></tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div style={{ height: 200, marginTop: 12 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={dadosEtapas} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                            <XAxis dataKey="etapa" tick={{ fontSize: 11 }} />
                                            <YAxis tick={{ fontSize: 11 }} tickFormatter={v => (v / 1000).toLocaleString('pt-BR') + 'k'} width={48} />
                                            <Tooltip formatter={v => moeda(v)} />
                                            <Bar dataKey="valor" radius={[4, 4, 0, 0]}>
                                                {dadosEtapas.map((d, i) => <Cell key={d.etapa} fill={CORES_ETAPA[i]} />)}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </Secao>

                            <Secao icone={<FaChartPie size={16} />} titulo="ND × NF dos fornecedores" sub="Pela marcação ND/NF do Efetivo (valor pago pela agência)">
                                <div className="fin-pp__tblwrap">
                                    <table className="fin-pp__tbl">
                                        <thead><tr><th>Documento</th><th style={{ textAlign: 'right' }}>Valor</th></tr></thead>
                                        <tbody>
                                            <tr><td className="fin-pp__cell-strong">ND</td><td style={{ textAlign: 'right' }}>{moeda(t.nd_nf_fornecedor.nd)}</td></tr>
                                            <tr><td className="fin-pp__cell-strong">NF</td><td style={{ textAlign: 'right' }}>{moeda(t.nd_nf_fornecedor.nf)}</td></tr>
                                            <tr><td className="fin-pp__cell-muted">Não informado</td><td style={{ textAlign: 'right' }}>{moeda(t.nd_nf_fornecedor.nao_informado)}</td></tr>
                                        </tbody>
                                    </table>
                                </div>
                                {dadosNdNf.length > 0 ? (
                                    <div style={{ height: 200, marginTop: 12 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie data={dadosNdNf} dataKey="valor" nameKey="nome" innerRadius={45} outerRadius={75} paddingAngle={2}>
                                                    {dadosNdNf.map(d => <Cell key={d.nome} fill={CORES_DOC[d.nome]} />)}
                                                </Pie>
                                                <Tooltip formatter={v => moeda(v)} />
                                                <Legend />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                ) : <div className="fin-pp__empty">Sem valores no orçamento.</div>}
                            </Secao>

                            <Secao icone={<FaFileAlt size={16} />} titulo="Notas fiscais de fornecedor" sub="Parcelas de fornecedor NF sem nota anexada (ND não entra)">
                                <div className="fin-pp__tblwrap">
                                    <table className="fin-pp__tbl">
                                        <thead><tr><th>Situação</th><th style={{ textAlign: 'right' }}>Qtd</th><th style={{ textAlign: 'right' }}>Valor</th></tr></thead>
                                        <tbody>
                                            <tr><td className="fin-pp__cell-strong">Parcelas sem NF</td><td style={{ textAlign: 'right', color: t.notas_fiscais.parcelas_sem_nf.qtd ? 'var(--fin-dg)' : undefined }}>{t.notas_fiscais.parcelas_sem_nf.qtd}</td><td style={{ textAlign: 'right' }}>{moeda(t.notas_fiscais.parcelas_sem_nf.valor)}</td></tr>
                                            <tr><td className="fin-pp__cell-muted" style={{ paddingLeft: 26 }}>já pagas sem NF</td><td style={{ textAlign: 'right', color: t.notas_fiscais.pagas_sem_nf.qtd ? 'var(--fin-dg)' : undefined }}>{t.notas_fiscais.pagas_sem_nf.qtd}</td><td style={{ textAlign: 'right' }}>{moeda(t.notas_fiscais.pagas_sem_nf.valor)}</td></tr>
                                            <tr><td className="fin-pp__cell-strong">Fornecedores NF sem nenhuma nota</td><td style={{ textAlign: 'right' }}>{t.notas_fiscais.fornecedores_nf_sem_nota}</td><td></td></tr>
                                        </tbody>
                                    </table>
                                </div>
                            </Secao>

                            <Secao icone={<FaHandHoldingUsd size={16} />} titulo="Adiantamento e saldo" sub="Dinheiro do cliente na conta do evento"
                                pill={<button type="button" className="fin-pp__btn fin-pp__btn--sm" style={{ marginLeft: 'auto' }} onClick={() => history.push('/financeiroSaldoAdiantamento')}>Saldos / Remanejar</button>}>
                                <div className="fin-pp__tblwrap">
                                    <table className="fin-pp__tbl">
                                        <tbody>
                                            <tr><td className="fin-pp__cell-strong">Solicitado, ainda não caiu</td><td style={{ textAlign: 'right', color: t.adiantamento.em_aberto ? 'var(--fin-wn)' : undefined }}>{moeda(t.adiantamento.em_aberto)}</td></tr>
                                            <tr><td className="fin-pp__cell-strong">Creditado (caiu)</td><td style={{ textAlign: 'right' }}>{moeda(t.adiantamento.creditado)}</td></tr>
                                            <tr><td className="fin-pp__cell-strong">Usado em fornecedores</td><td style={{ textAlign: 'right' }}>{moeda(t.adiantamento.usado_fornecedores)}</td></tr>
                                            <tr><td className="fin-pp__cell-strong">Disponível</td><td style={{ textAlign: 'right', fontWeight: 700, color: t.adiantamento.disponivel < 0 ? 'var(--fin-dg)' : 'var(--fin-ok)' }}>{moeda(t.adiantamento.disponivel)}</td></tr>
                                        </tbody>
                                    </table>
                                </div>
                            </Secao>

                            <Secao icone={<FaUniversity size={16} />} titulo="Faturamento ao cliente" sub="ND e NF emitidas para o cliente">
                                <div className="fin-pp__tblwrap">
                                    <table className="fin-pp__tbl">
                                        <thead><tr><th>Documento</th><th style={{ textAlign: 'right' }}>Qtd</th><th style={{ textAlign: 'right' }}>Emitido</th><th style={{ textAlign: 'right' }}>Recebido</th><th style={{ textAlign: 'right' }}>Em aberto</th></tr></thead>
                                        <tbody>
                                            {['nd', 'nf'].map(k => (
                                                <tr key={k}>
                                                    <td className="fin-pp__cell-strong">{k.toUpperCase()}</td>
                                                    <td style={{ textAlign: 'right' }}>{t.faturamento_cliente[k].qtd}</td>
                                                    <td style={{ textAlign: 'right' }}>{moeda(t.faturamento_cliente[k].valor)}</td>
                                                    <td style={{ textAlign: 'right', color: 'var(--fin-ok)' }}>{moeda(t.faturamento_cliente[k].recebido)}</td>
                                                    <td style={{ textAlign: 'right', color: t.faturamento_cliente[k].em_aberto ? 'var(--fin-wn)' : undefined }}>{moeda(t.faturamento_cliente[k].em_aberto)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </Secao>
                        </div>

                        {!ehEvento && (
                            <Secao icone={<FaListUl size={16} />} titulo="Eventos do cliente" sub={dados.eventos.length + ' eventos no período · clique para ver o raio-X do evento'}>
                                <div className="fin-pp__tblwrap">
                                    <table className="fin-pp__tbl">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '38%' }}>Evento</th><th>Data</th>
                                                <th style={{ textAlign: 'right' }}>Orçado</th>
                                                <th style={{ textAlign: 'right' }}>Pago</th>
                                                <th style={{ textAlign: 'right' }}>A pagar</th>
                                                <th style={{ textAlign: 'right' }}>Sem NF</th>
                                                <th style={{ textAlign: 'right' }}>Saldo</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {dados.eventos.map(e => (
                                                <tr key={e.evento.id} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} title="Ver raio-X do evento" onClick={() => abrirEvento(e.evento)}>
                                                    <td className="fin-pp__cell-strong" style={{ whiteSpace: 'normal' }}>{e.evento.codigo ? '#' + e.evento.codigo + ' ' : ''}{e.evento.nome}</td>
                                                    <td className="fin-pp__cell-muted">{dataBr(e.evento.data)}</td>
                                                    <td style={{ textAlign: 'right' }}>{moeda(e.orcamento.total)}</td>
                                                    <td style={{ textAlign: 'right' }}>{moeda(e.contas_pagar.pagas.valor)}</td>
                                                    <td style={{ textAlign: 'right' }}>{moeda(e.contas_pagar.a_pagar.valor)}</td>
                                                    <td style={{ textAlign: 'right', color: e.notas_fiscais.parcelas_sem_nf.qtd ? 'var(--fin-dg)' : undefined }}>{e.notas_fiscais.parcelas_sem_nf.qtd}</td>
                                                    <td style={{ textAlign: 'right', color: e.saldo < 0 ? 'var(--fin-dg)' : undefined }}>{moeda(e.saldo)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </Secao>
                        )}
                    </>
                )}
            </div>
        </ErpTemplate>
    );
}
